import config from 'constants/config'
import { OFFER_TYPE_BED_BANK, OFFER_TYPE_HOTEL } from 'constants/offer'

export const SUPPORTED_CHANNEL_MARKUP_OFFER_TYPES = [OFFER_TYPE_HOTEL, OFFER_TYPE_BED_BANK]
export const DEFAULT_CHANNEL_MARKUP_VALUE = 1
const ROUND_UP_MULTIPLE = 10
const ALLOWED_UTM_SOURCE = ['impact_radius', 'google', 'facebook']

export type ChannelMarkupDetails = {
  markup: number
  isApplied: boolean
}

const NO_CHANNEL_MARKUP: ChannelMarkupDetails = { markup: DEFAULT_CHANNEL_MARKUP_VALUE, isApplied: false }

/**
 * Round a numeric value up to the nearest multiple of 10.
 * Examples:
 * - 4198.95 -> 4200
 * - 3255.34 -> 3260
 * - 770     -> 770
 *
 * Accepts number or numeric string input to mirror floatify-style helpers.
 */
export function roundUpToNearestTen(value: number | string): number {
  const num = parseFloat(value as any)
  if (!Number.isFinite(num)) {
    // Mirror the behaviour of parseFloat-style helpers: return NaN (or +/-Infinity) as-is.
    return num
  }
  return Math.ceil(num / ROUND_UP_MULTIPLE) * ROUND_UP_MULTIPLE
}

function isOfferEligibleForChannelMarkup(
  offerId: string,
  offerType?: App.OfferType,
  channelMarkup?: App.ChannelMarkup,
): boolean {
  if (!offerType || !channelMarkup) {
    return false
  }
  if (channelMarkup.channelMarkupBlacklist.includes(offerId)) {
    return false
  }
  // LPP (bedbank) channel markup is behind its own kill-switch, mirroring svc-order's
  // ENABLE_CHANNEL_MARKUP_FOR_LPP flag, so it can be rolled out / reverted independently.
  if (offerType === OFFER_TYPE_BED_BANK && !config.ENABLE_CHANNEL_MARKUP_FOR_LPP) {
    return false
  }
  return SUPPORTED_CHANNEL_MARKUP_OFFER_TYPES.includes(offerType)
}

export function getChannelMarkupDetails(
  offerId: string,
  offerType?: App.OfferType,
  channelMarkup?: App.ChannelMarkup,
): ChannelMarkupDetails {
  if (!isOfferEligibleForChannelMarkup(offerId, offerType, channelMarkup)) {
    return NO_CHANNEL_MARKUP
  }

  const channelMarkupValue = channelMarkup!.channelMarkupValue
  const isApplied = !!channelMarkupValue && channelMarkupValue !== DEFAULT_CHANNEL_MARKUP_VALUE
  return {
    markup: isApplied ? channelMarkupValue : DEFAULT_CHANNEL_MARKUP_VALUE,
    isApplied,
  }
}

export function getChannelMarkupValue(
  offerId: string,
  offerType?: App.OfferType,
  channelMarkup?: App.ChannelMarkup,
): number {
  return getChannelMarkupDetails(offerId, offerType, channelMarkup).markup
}

export function getMemberPriceChannelMarkupDetails(
  offerId: string,
  offerType?: App.OfferType,
  channelMarkup?: App.ChannelMarkup,
): ChannelMarkupDetails {
  if (!config.ENABLE_CHANNEL_MARKUP_FOR_MEMBER_PRICE) {
    return NO_CHANNEL_MARKUP
  }
  if (!isOfferEligibleForChannelMarkup(offerId, offerType, channelMarkup)) {
    return NO_CHANNEL_MARKUP
  }

  const luxplusMarkup = channelMarkup?.luxplusMarkupValue
  if (!luxplusMarkup) {
    return NO_CHANNEL_MARKUP
  }
  return { markup: luxplusMarkup, isApplied: true }
}

export function isAllowedChannelMarkupQueryParams(filters: App.ChannelMarkupFilters) {
  return ALLOWED_UTM_SOURCE.includes(filters.source)
}

export function applyChannelMarkupAndRoundUpToTen(value: number, markupDetails: ChannelMarkupDetails): number {
  if (!markupDetails.isApplied) {
    // No channel markup applied: return original price untouched.
    return value
  }

  const updatedValue = value * markupDetails.markup
  return roundUpToNearestTen(updatedValue)
}
